import { supabase } from '../lib/supabase';

export type MappingType = 'input' | 'output';
export type EntityType =
  | 'match'
  | 'match_stats'
  | 'player'
  | 'stat'
  | 'highlight'
  | 'tactical_chart'
  | 'match_list'
  | 'create_match';

export interface OrganisationDataMapping {
  id: string;
  organisation_id: string;
  mapping_type: MappingType;
  entity_type: EntityType;
  source_field: string;
  target_field: string;
  transformation_rule: Record<string, unknown> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MappingConfig {
  source_field: string;
  target_field: string;
  transformation_rule?: Record<string, unknown>;
}

type PathSegment = { key: string; isArray: boolean };

function parsePath(path: string): PathSegment[] {
  return path.split('.').map((part) => {
    if (part.endsWith('[]')) {
      return { key: part.slice(0, -2), isArray: true };
    }
    return { key: part, isArray: false };
  });
}

function hasArrayPath(path: string): boolean {
  return path.includes('[]');
}

function isStatsWildcardPath(path: string): boolean {
  return path.startsWith('stats.*.');
}

function isStatisticsArrayPath(path: string): boolean {
  return path.startsWith('statistics[].');
}

function isPlayerStatsArrayPath(path: string): boolean {
  return path.startsWith('stats[].');
}

function isMatchTeamWildcardPath(path: string): boolean {
  return path.startsWith('match.team.');
}

function expandMatchTeamPath(path: string, teamKey: 'home_team' | 'away_team'): string {
  return path.replace('match.team.', `match.${teamKey}.`);
}

function isScoreSummaryTeamWildcardPath(path: string): boolean {
  return path.startsWith('match.score_summary.team.');
}

function expandScoreSummaryTeamPath(path: string, teamKey: 'home_team' | 'away_team'): string {
  return path.replace('match.score_summary.team.', `match.score_summary.${teamKey}.`);
}

/**
 * Get value at path (no array markers).
 */
function getAtPath(obj: Record<string, unknown>, path: string): unknown {
  const parts = path.split('.');
  let current: unknown = obj;
  for (const p of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[p];
  }
  return current;
}

/**
 * Set value at path; creates nested objects as needed (no array markers).
 */
function setAtPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const nextKey = parts[i + 1];
    if (current[key] == null || typeof current[key] !== 'object') {
      (current as Record<string, unknown>)[key] = /^\d+$/.test(nextKey) ? [] : {};
    }
    current = (current[key] as Record<string, unknown>);
  }
  (current as Record<string, unknown>)[parts[parts.length - 1]] = value;
}

/**
 * Delete a value at path; removes empty objects along the way.
 */
function deleteAtPath(obj: Record<string, unknown>, path: string): void {
  const parts = path.split('.');
  const stack: Array<{ parent: Record<string, unknown>; key: string }> = [];
  let current: unknown = obj;
  for (const p of parts) {
    if (current == null || typeof current !== 'object') return;
    stack.push({ parent: current as Record<string, unknown>, key: p });
    current = (current as Record<string, unknown>)[p];
  }
  while (stack.length) {
    const { parent, key } = stack.pop()!;
    if (parent && typeof parent === 'object') {
      delete parent[key];
      if (Object.keys(parent).length > 0) break;
    }
  }
}

/** Build path string from segments (e.g. for parent path). */
function pathSegmentsToPath(segments: PathSegment[]): string {
  return segments.map((s) => (s.isArray ? s.key + '[]' : s.key)).join('.');
}

/** Parent path of the given path (path to the object containing the last segment). */
function parentPathOf(path: string): string {
  const segs = parsePath(path);
  if (segs.length <= 1) return '';
  return pathSegmentsToPath(segs.slice(0, -1));
}

interface PathRenameRecord {
  parentPath: string;
  fromKey: string;
  toKey: string;
}

/** Resolve path to where it currently lives in result after earlier renames. */
function resolvePathInResult(path: string, renames: PathRenameRecord[]): string {
  const segments = parsePath(path);
  if (segments.length === 0) return path;
  const parts: string[] = [];
  let currentParent = '';
  for (const seg of segments) {
    let key = seg.key;
    for (const r of renames) {
      if (r.parentPath === currentParent && r.fromKey === key) {
        key = r.toKey;
        break;
      }
    }
    parts.push(seg.isArray ? key + '[]' : key);
    currentParent = currentParent ? currentParent + '.' + parts[parts.length - 1]! : parts[parts.length - 1]!;
  }
  return parts.join('.');
}

function sameParentPath(sourcePath: string, targetPath: string): boolean {
  const sourceSegs = parsePath(sourcePath);
  const targetSegs = parsePath(targetPath);
  if (sourceSegs.length !== targetSegs.length || sourceSegs.length === 0) return false;
  for (let i = 0; i < sourceSegs.length - 1; i++) {
    if (sourceSegs[i].key !== targetSegs[i].key || sourceSegs[i].isArray !== targetSegs[i].isArray) return false;
  }
  return true;
}

function replaceObjectKeepingRef(target: Record<string, unknown>, next: Record<string, unknown>): void {
  for (const key of Object.keys(target)) delete target[key];
  Object.assign(target, next);
}

function renameLeafKeyPreserveOrder(target: Record<string, unknown>, sourcePath: string, targetPath: string): boolean {
  if (!sameParentPath(sourcePath, targetPath)) return false;
  const segs = parsePath(sourcePath);
  const targetSegs = parsePath(targetPath);
  const sourceKey = segs[segs.length - 1].key;
  const targetKey = targetSegs[targetSegs.length - 1].key;
  if (sourceKey === targetKey) return false;

  const parentSegs = segs.slice(0, -1);
  const walk = (current: unknown, idx: number) => {
    if (current == null || typeof current !== 'object') return;
    if (idx >= parentSegs.length) {
      const parent = current as Record<string, unknown>;
      if (!(sourceKey in parent)) return;
      const next: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(parent)) {
        if (k === sourceKey) {
          next[targetKey] = v;
        } else {
          next[k] = v;
        }
      }
      replaceObjectKeepingRef(parent, next);
      return;
    }
    const seg = parentSegs[idx];
    if (seg.isArray) {
      const arr = (current as Record<string, unknown>)[seg.key];
      if (!Array.isArray(arr)) return;
      for (const item of arr) walk(item as Record<string, unknown>, idx + 1);
      return;
    }
    walk((current as Record<string, unknown>)[seg.key], idx + 1);
  };

  walk(target, 0);
  return true;
}

/**
 * Apply mapping for paths with array markers (e.g. "items[].name").
 * Assumes source and target paths align in structure.
 */
function applyMappingWithArrays(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): void {
  const sourceSegs = parsePath(sourcePath);
  const targetSegs = parsePath(targetPath);

  const walk = (src: unknown, dst: Record<string, unknown>, idx: number) => {
    if (idx >= sourceSegs.length) return;
    const sSeg = sourceSegs[idx];
    const tSeg = targetSegs[idx] || sSeg;
    if (sSeg.isArray) {
      const arr = (src as Record<string, unknown> | undefined)?.[sSeg.key];
      if (!Array.isArray(arr)) return;
      if (dst[tSeg.key] == null) dst[tSeg.key] = [];
      const outArr = dst[tSeg.key] as unknown[];
      for (let i = 0; i < arr.length; i++) {
        if (outArr[i] == null || typeof outArr[i] !== 'object') {
          outArr[i] = {};
        }
        walk(arr[i] as Record<string, unknown>, outArr[i] as Record<string, unknown>, idx + 1);
      }
      return;
    }

    const next = (src as Record<string, unknown> | undefined)?.[sSeg.key];
    if (next === undefined) return;
    if (idx === sourceSegs.length - 1) {
      dst[tSeg.key] = next;
      return;
    }
    if (dst[tSeg.key] == null || typeof dst[tSeg.key] !== 'object') {
      dst[tSeg.key] = {};
    }
    walk(next as Record<string, unknown>, dst[tSeg.key] as Record<string, unknown>, idx + 1);
  };

  walk(source, target, 0);
}

function deleteMappingWithArrays(
  target: Record<string, unknown>,
  sourcePath: string
): void {
  const segs = parsePath(sourcePath);
  const walk = (dst: unknown, idx: number) => {
    if (idx >= segs.length) return;
    const seg = segs[idx];
    if (seg.isArray) {
      const arr = (dst as Record<string, unknown> | undefined)?.[seg.key];
      if (!Array.isArray(arr)) return;
      for (const item of arr) walk(item as Record<string, unknown>, idx + 1);
      return;
    }
    if (idx === segs.length - 1) {
      if (dst && typeof dst === 'object') {
        delete (dst as Record<string, unknown>)[seg.key];
      }
      return;
    }
    const next = (dst as Record<string, unknown> | undefined)?.[seg.key];
    if (next == null || typeof next !== 'object') return;
    walk(next as Record<string, unknown>, idx + 1);
  };
  walk(target, 0);
}

function applyStatsWildcardMapping(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): void {
  const sourceField = sourcePath.replace(/^stats\.\*\./, '');
  const targetField = targetPath.replace(/^stats\.\*\./, '');
  const stats = source.stats;
  if (!stats || typeof stats !== 'object') return;
  if (target.stats == null || typeof target.stats !== 'object') {
    target.stats = {};
  }
  for (const [statKey, statObj] of Object.entries(stats as Record<string, unknown>)) {
    if (!statObj || typeof statObj !== 'object') continue;
    const value = (statObj as Record<string, unknown>)[sourceField];
    if (value === undefined) continue;
    if ((target.stats as Record<string, unknown>)[statKey] == null || typeof (target.stats as Record<string, unknown>)[statKey] !== 'object') {
      (target.stats as Record<string, unknown>)[statKey] = {};
    }
    (target.stats as Record<string, unknown>)[statKey] = {
      ...(target.stats as Record<string, unknown>)[statKey] as Record<string, unknown>,
      [targetField]: value,
    };
  }
}

function deleteStatsWildcardMapping(target: Record<string, unknown>, sourcePath: string): void {
  const sourceField = sourcePath.replace(/^stats\.\*\./, '');
  const stats = target.stats;
  if (!stats || typeof stats !== 'object') return;
  for (const [, statObj] of Object.entries(stats as Record<string, unknown>)) {
    if (!statObj || typeof statObj !== 'object') continue;
    delete (statObj as Record<string, unknown>)[sourceField];
  }
}

function renameStatsWildcardPreserveOrder(
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): boolean {
  if (!isStatsWildcardPath(sourcePath) || !isStatsWildcardPath(targetPath)) return false;
  const sourceField = sourcePath.replace(/^stats\.\*\./, '');
  const targetField = targetPath.replace(/^stats\.\*\./, '');
  if (sourceField === targetField) return false;
  const stats = target.stats;
  if (!stats || typeof stats !== 'object') return false;
  for (const [, statObj] of Object.entries(stats as Record<string, unknown>)) {
    if (!statObj || typeof statObj !== 'object') continue;
    if (!(sourceField in (statObj as Record<string, unknown>))) continue;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(statObj as Record<string, unknown>)) {
      if (k === sourceField) {
        next[targetField] = v;
      } else {
        next[k] = v;
      }
    }
    replaceObjectKeepingRef(statObj as Record<string, unknown>, next);
  }
  return true;
}

function applyStatisticsArrayMapping(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): void {
  const sourceField = sourcePath.replace(/^statistics\[\]\./, '');
  const targetField = targetPath.replace(/^statistics\[\]\./, '');
  const statistics = source.statistics as Record<string, unknown> | undefined;
  if (!statistics || typeof statistics !== 'object') return;
  if (target.statistics == null || typeof target.statistics !== 'object') {
    target.statistics = {};
  }
  const arrays = ['home_team_stats', 'away_team_stats'] as const;
  for (const key of arrays) {
    const arr = statistics[key];
    if (!Array.isArray(arr)) continue;
    const outArr = Array.isArray((target.statistics as Record<string, unknown>)[key])
      ? ((target.statistics as Record<string, unknown>)[key] as unknown[])
      : [];
    for (let i = 0; i < arr.length; i++) {
      const item = arr[i] as Record<string, unknown>;
      if (!item || typeof item !== 'object') continue;
      const value = item[sourceField];
      if (value === undefined) continue;
      if (!outArr[i] || typeof outArr[i] !== 'object') outArr[i] = {};
      (outArr[i] as Record<string, unknown>)[targetField] = value;
    }
    (target.statistics as Record<string, unknown>)[key] = outArr;
  }
}

function deleteStatisticsArrayMapping(target: Record<string, unknown>, sourcePath: string): void {
  const sourceField = sourcePath.replace(/^statistics\[\]\./, '');
  const statistics = target.statistics as Record<string, unknown> | undefined;
  if (!statistics || typeof statistics !== 'object') return;
  const arrays = ['home_team_stats', 'away_team_stats'] as const;
  for (const key of arrays) {
    const arr = statistics[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (!item || typeof item !== 'object') continue;
      delete (item as Record<string, unknown>)[sourceField];
    }
  }
}

function renameStatisticsArrayPreserveOrder(
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): boolean {
  if (!isStatisticsArrayPath(sourcePath) || !isStatisticsArrayPath(targetPath)) return false;
  const sourceField = sourcePath.replace(/^statistics\[\]\./, '');
  const targetField = targetPath.replace(/^statistics\[\]\./, '');
  if (sourceField === targetField) return false;
  const statistics = target.statistics as Record<string, unknown> | undefined;
  if (!statistics || typeof statistics !== 'object') return false;
  const arrays = ['home_team_stats', 'away_team_stats'] as const;
  for (const key of arrays) {
    const arr = statistics[key];
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (!item || typeof item !== 'object') continue;
      if (!(sourceField in (item as Record<string, unknown>))) continue;
      const next: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
        if (k === sourceField) {
          next[targetField] = v;
        } else {
          next[k] = v;
        }
      }
      replaceObjectKeepingRef(item as Record<string, unknown>, next);
    }
  }
  return true;
}

function applyPlayerStatsArrayMapping(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): void {
  const sourceField = sourcePath.replace(/^stats\[\]\./, '');
  const targetField = targetPath.replace(/^stats\[\]\./, '');
  const stats = source.stats;
  if (!Array.isArray(stats)) return;
  if (!Array.isArray(target.stats)) target.stats = [];
  const outArr = target.stats as Array<Record<string, unknown>>;
  for (let i = 0; i < stats.length; i++) {
    const item = stats[i] as Record<string, unknown>;
    if (!item || typeof item !== 'object') continue;
    const value = item[sourceField];
    if (value === undefined) continue;
    if (!outArr[i] || typeof outArr[i] !== 'object') {
      outArr[i] = { ...item };
    }
    outArr[i][targetField] = value;
  }
}

function deletePlayerStatsArrayMapping(target: Record<string, unknown>, sourcePath: string): void {
  const sourceField = sourcePath.replace(/^stats\[\]\./, '');
  const stats = target.stats;
  if (!Array.isArray(stats)) return;
  for (const item of stats) {
    if (!item || typeof item !== 'object') continue;
    delete (item as Record<string, unknown>)[sourceField];
  }
}

function renamePlayerStatsArrayPreserveOrder(
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string
): boolean {
  if (!isPlayerStatsArrayPath(sourcePath) || !isPlayerStatsArrayPath(targetPath)) return false;
  const sourceField = sourcePath.replace(/^stats\[\]\./, '');
  const targetField = targetPath.replace(/^stats\[\]\./, '');
  if (sourceField === targetField) return false;
  const stats = target.stats;
  if (!Array.isArray(stats)) return false;
  for (const item of stats) {
    if (!item || typeof item !== 'object') continue;
    if (!(sourceField in (item as Record<string, unknown>))) continue;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(item as Record<string, unknown>)) {
      if (k === sourceField) {
        next[targetField] = v;
      } else {
        next[k] = v;
      }
    }
    replaceObjectKeepingRef(item as Record<string, unknown>, next);
  }
  return true;
}

function applyMatchTeamWildcardMapping(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string,
  pathRenames?: PathRenameRecord[]
): void {
  const teams: Array<'home_team' | 'away_team'> = ['home_team', 'away_team'];
  for (const team of teams) {
    const sourceExpanded = expandMatchTeamPath(sourcePath, team);
    let targetExpanded = isMatchTeamWildcardPath(targetPath)
      ? expandMatchTeamPath(targetPath, team)
      : targetPath;
    if (pathRenames?.length) targetExpanded = resolvePathInResult(targetExpanded, pathRenames);
    if (hasArrayPath(sourceExpanded) || hasArrayPath(targetExpanded)) {
      applyMappingWithArrays(source, target, sourceExpanded, targetExpanded);
      continue;
    }
    const value = getAtPath(source, sourceExpanded);
    if (value !== undefined) setAtPath(target, targetExpanded, value);
  }
}

function deleteMatchTeamWildcardMapping(
  target: Record<string, unknown>,
  sourcePath: string,
  pathRenames?: PathRenameRecord[]
): void {
  const teams: Array<'home_team' | 'away_team'> = ['home_team', 'away_team'];
  for (const team of teams) {
    let sourceExpanded = expandMatchTeamPath(sourcePath, team);
    if (pathRenames?.length) sourceExpanded = resolvePathInResult(sourceExpanded, pathRenames);
    if (hasArrayPath(sourceExpanded)) {
      deleteMappingWithArrays(target, sourceExpanded);
      continue;
    }
    deleteAtPath(target, sourceExpanded);
  }
}

function applyScoreSummaryTeamWildcardMapping(
  source: Record<string, unknown>,
  target: Record<string, unknown>,
  sourcePath: string,
  targetPath: string,
  pathRenames?: PathRenameRecord[]
): void {
  const teams: Array<'home_team' | 'away_team'> = ['home_team', 'away_team'];
  for (const team of teams) {
    const sourceExpanded = expandScoreSummaryTeamPath(sourcePath, team);
    let targetExpanded = isScoreSummaryTeamWildcardPath(targetPath)
      ? expandScoreSummaryTeamPath(targetPath, team)
      : targetPath;
    if (pathRenames?.length) targetExpanded = resolvePathInResult(targetExpanded, pathRenames);
    if (hasArrayPath(sourceExpanded) || hasArrayPath(targetExpanded)) {
      applyMappingWithArrays(source, target, sourceExpanded, targetExpanded);
      continue;
    }
    const value = getAtPath(source, sourceExpanded);
    if (value !== undefined) setAtPath(target, targetExpanded, value);
  }
}

function deleteScoreSummaryTeamWildcardMapping(
  target: Record<string, unknown>,
  sourcePath: string,
  pathRenames?: PathRenameRecord[]
): void {
  const teams: Array<'home_team' | 'away_team'> = ['home_team', 'away_team'];
  for (const team of teams) {
    let sourceExpanded = expandScoreSummaryTeamPath(sourcePath, team);
    if (pathRenames?.length) sourceExpanded = resolvePathInResult(sourceExpanded, pathRenames);
    if (hasArrayPath(sourceExpanded)) {
      deleteMappingWithArrays(target, sourceExpanded);
      continue;
    }
    deleteAtPath(target, sourceExpanded);
  }
}

/**
 * Deep clone a plain object (no functions/dates).
 */
function deepClone(obj: unknown): unknown {
  if (obj == null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(deepClone);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = deepClone(v);
  }
  return out;
}

/**
 * Fetch active mappings for an organisation and type.
 */
async function getMappings(
  organisationId: string,
  mappingType: MappingType,
  entityType?: EntityType
): Promise<OrganisationDataMapping[]> {
  let query = supabase
    .from('organisation_data_mappings')
    .select('*')
    .eq('organisation_id', organisationId)
    .eq('mapping_type', mappingType)
    .eq('is_active', true);

  if (entityType) {
    query = query.eq('entity_type', entityType);
  }

  const { data, error } = await query.order('source_field');

  if (error) {
    console.error('clientDataMappingService.getMappings error:', error);
    return [];
  }
  return (data as OrganisationDataMapping[]) ?? [];
}

/**
 * Map client input format to internal format using input mappings.
 * Mappings: source_field = client path, target_field = internal path.
 */
export async function mapInput(
  organisationId: string,
  entityType: EntityType,
  clientData: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const mappings = await getMappings(organisationId, 'input', entityType);
  if (mappings.length === 0) {
    return deepClone(clientData) as Record<string, unknown>;
  }

  const result: Record<string, unknown> = {};
  for (const m of mappings) {
    if (isScoreSummaryTeamWildcardPath(m.source_field) || isScoreSummaryTeamWildcardPath(m.target_field)) {
      applyScoreSummaryTeamWildcardMapping(clientData, result, m.source_field, m.target_field);
    } else if (isMatchTeamWildcardPath(m.source_field) || isMatchTeamWildcardPath(m.target_field)) {
      applyMatchTeamWildcardMapping(clientData, result, m.source_field, m.target_field);
    } else if (isStatsWildcardPath(m.source_field) || isStatsWildcardPath(m.target_field)) {
      applyStatsWildcardMapping(clientData, result, m.source_field, m.target_field);
    } else if (isPlayerStatsArrayPath(m.source_field) || isPlayerStatsArrayPath(m.target_field)) {
      applyPlayerStatsArrayMapping(clientData, result, m.source_field, m.target_field);
    } else if (isStatisticsArrayPath(m.source_field) || isStatisticsArrayPath(m.target_field)) {
      applyStatisticsArrayMapping(clientData, result, m.source_field, m.target_field);
    } else if (hasArrayPath(m.source_field) || hasArrayPath(m.target_field)) {
      applyMappingWithArrays(clientData, result, m.source_field, m.target_field);
    } else {
      const value = getAtPath(clientData, m.source_field);
      if (value !== undefined) {
        setAtPath(result, m.target_field, value);
      }
    }
  }
  return result;
}

/**
 * Map internal format to client output format using output mappings.
 * Mappings: source_field = internal path, target_field = client path.
 */
export async function mapOutput(
  organisationId: string,
  entityType: EntityType,
  internalData: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const mappings = await getMappings(organisationId, 'output', entityType);
  if (mappings.length === 0) {
    return deepClone(internalData) as Record<string, unknown>;
  }
  const result = deepClone(internalData) as Record<string, unknown>;
  const pathRenames: PathRenameRecord[] = [];

  for (const m of mappings) {
    const shouldDeleteSource = m.source_field !== m.target_field;
    if (isScoreSummaryTeamWildcardPath(m.source_field) || isScoreSummaryTeamWildcardPath(m.target_field)) {
      if (shouldDeleteSource) {
        const targetExpandedHome = isScoreSummaryTeamWildcardPath(m.target_field)
          ? expandScoreSummaryTeamPath(m.target_field, 'home_team')
          : m.target_field;
        const targetExpandedAway = isScoreSummaryTeamWildcardPath(m.target_field)
          ? expandScoreSummaryTeamPath(m.target_field, 'away_team')
          : m.target_field;
        const okHome = renameLeafKeyPreserveOrder(
          result,
          resolvePathInResult(expandScoreSummaryTeamPath(m.source_field, 'home_team'), pathRenames),
          targetExpandedHome
        );
        const okAway = renameLeafKeyPreserveOrder(
          result,
          resolvePathInResult(expandScoreSummaryTeamPath(m.source_field, 'away_team'), pathRenames),
          targetExpandedAway
        );
        if (okHome || okAway) {
          const sourceSegs = parsePath(m.source_field);
          const targetSegs = parsePath(m.target_field);
          const fromKey = sourceSegs[sourceSegs.length - 1]?.key ?? '';
          const toKey = targetSegs[targetSegs.length - 1]?.key ?? '';
          if (fromKey && toKey) {
            pathRenames.push({
              parentPath: parentPathOf(expandScoreSummaryTeamPath(m.source_field, 'home_team')),
              fromKey,
              toKey,
            });
            pathRenames.push({
              parentPath: parentPathOf(expandScoreSummaryTeamPath(m.source_field, 'away_team')),
              fromKey,
              toKey,
            });
          }
          continue;
        }
      }
      applyScoreSummaryTeamWildcardMapping(internalData, result, m.source_field, m.target_field, pathRenames);
      if (shouldDeleteSource) deleteScoreSummaryTeamWildcardMapping(result, m.source_field, pathRenames);
    } else if (isMatchTeamWildcardPath(m.source_field) || isMatchTeamWildcardPath(m.target_field)) {
      if (shouldDeleteSource) {
        const targetExpandedHome = isMatchTeamWildcardPath(m.target_field)
          ? expandMatchTeamPath(m.target_field, 'home_team')
          : m.target_field;
        const targetExpandedAway = isMatchTeamWildcardPath(m.target_field)
          ? expandMatchTeamPath(m.target_field, 'away_team')
          : m.target_field;
        const okHome = renameLeafKeyPreserveOrder(
          result,
          resolvePathInResult(expandMatchTeamPath(m.source_field, 'home_team'), pathRenames),
          targetExpandedHome
        );
        const okAway = renameLeafKeyPreserveOrder(
          result,
          resolvePathInResult(expandMatchTeamPath(m.source_field, 'away_team'), pathRenames),
          targetExpandedAway
        );
        if (okHome || okAway) {
          const sourceSegs = parsePath(m.source_field);
          const targetSegs = parsePath(m.target_field);
          const fromKey = sourceSegs[sourceSegs.length - 1]?.key ?? '';
          const toKey = targetSegs[targetSegs.length - 1]?.key ?? '';
          if (fromKey && toKey) {
            pathRenames.push({
              parentPath: parentPathOf(expandMatchTeamPath(m.source_field, 'home_team')),
              fromKey,
              toKey,
            });
            pathRenames.push({
              parentPath: parentPathOf(expandMatchTeamPath(m.source_field, 'away_team')),
              fromKey,
              toKey,
            });
          }
          continue;
        }
      }
      applyMatchTeamWildcardMapping(internalData, result, m.source_field, m.target_field, pathRenames);
      if (shouldDeleteSource) deleteMatchTeamWildcardMapping(result, m.source_field, pathRenames);
    } else if (isStatsWildcardPath(m.source_field) || isStatsWildcardPath(m.target_field)) {
      if (shouldDeleteSource && renameStatsWildcardPreserveOrder(result, m.source_field, m.target_field)) {
        continue;
      }
      applyStatsWildcardMapping(internalData, result, m.source_field, m.target_field);
      if (shouldDeleteSource) deleteStatsWildcardMapping(result, m.source_field);
    } else if (isPlayerStatsArrayPath(m.source_field) || isPlayerStatsArrayPath(m.target_field)) {
      if (shouldDeleteSource && renamePlayerStatsArrayPreserveOrder(result, m.source_field, m.target_field)) {
        continue;
      }
      applyPlayerStatsArrayMapping(internalData, result, m.source_field, m.target_field);
      if (shouldDeleteSource) deletePlayerStatsArrayMapping(result, m.source_field);
    } else if (isStatisticsArrayPath(m.source_field) || isStatisticsArrayPath(m.target_field)) {
      if (shouldDeleteSource && renameStatisticsArrayPreserveOrder(result, m.source_field, m.target_field)) {
        continue;
      }
      applyStatisticsArrayMapping(internalData, result, m.source_field, m.target_field);
      if (shouldDeleteSource) deleteStatisticsArrayMapping(result, m.source_field);
    } else if (hasArrayPath(m.source_field) || hasArrayPath(m.target_field)) {
      if (shouldDeleteSource && renameLeafKeyPreserveOrder(result, m.source_field, m.target_field)) {
        continue;
      }
      applyMappingWithArrays(internalData, result, m.source_field, m.target_field);
      if (shouldDeleteSource) deleteMappingWithArrays(result, m.source_field);
    } else {
      if (shouldDeleteSource && renameLeafKeyPreserveOrder(result, m.source_field, m.target_field)) {
        continue;
      }
      const value = getAtPath(internalData, m.source_field);
      if (value !== undefined) {
        setAtPath(result, m.target_field, value);
        if (shouldDeleteSource) deleteAtPath(result, m.source_field);
      }
    }
  }
  return result;
}

/**
 * Map internal stat key to client stat name (e.g. "goals" -> "goal").
 * Uses output mappings with entity_type = 'stat'.
 */
export async function mapStatName(
  organisationId: string,
  internalStatKey: string
): Promise<string> {
  const mappings = await getMappings(organisationId, 'output', 'stat');
  const found = mappings.find((m) => m.source_field === internalStatKey);
  return found ? found.target_field : internalStatKey;
}

/**
 * Bulk import mappings for an organisation (replaces existing for same type/entity).
 * Used by Admin UI or seed scripts.
 */
export async function importMappings(
  organisationId: string,
  mappingType: MappingType,
  entityType: EntityType,
  mappings: MappingConfig[]
): Promise<{ error: string | null }> {
  try {
    const rows = mappings.map((m) => ({
      organisation_id: organisationId,
      mapping_type: mappingType,
      entity_type: entityType,
      source_field: m.source_field,
      target_field: m.target_field,
      transformation_rule: m.transformation_rule ?? null,
      is_active: true,
    }));

    const { error } = await supabase.from('organisation_data_mappings').insert(rows);
    if (error) return { error: error.message };
    return { error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown error';
    return { error: message };
  }
}

/**
 * List all mappings for an organisation (for Admin UI).
 */
export async function listMappings(organisationId: string): Promise<OrganisationDataMapping[]> {
  const { data, error } = await supabase
    .from('organisation_data_mappings')
    .select('*')
    .eq('organisation_id', organisationId)
    .order('mapping_type')
    .order('entity_type')
    .order('source_field');

  if (error) {
    console.error('clientDataMappingService.listMappings error:', error);
    return [];
  }
  return (data as OrganisationDataMapping[]) ?? [];
}

/**
 * Create a single mapping (for Admin UI).
 */
export async function createMapping(
  organisationId: string,
  mappingType: MappingType,
  entityType: EntityType,
  sourceField: string,
  targetField: string,
  transformationRule?: Record<string, unknown> | null
): Promise<{ data: OrganisationDataMapping | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('organisation_data_mappings')
      .insert({
        organisation_id: organisationId,
        mapping_type: mappingType,
        entity_type: entityType,
        source_field: sourceField,
        target_field: targetField,
        transformation_rule: transformationRule ?? null,
        is_active: true,
      })
      .select()
      .single();

    if (error) return { data: null, error: error.message };
    return { data: data as OrganisationDataMapping, error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown error';
    return { data: null, error: message };
  }
}

/**
 * Update a single mapping (for Admin UI).
 */
export async function updateMapping(
  mappingId: string,
  updates: { source_field?: string; target_field?: string; transformation_rule?: Record<string, unknown> | null; is_active?: boolean }
): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase
      .from('organisation_data_mappings')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', mappingId);

    if (error) return { error: error.message };
    return { error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown error';
    return { error: message };
  }
}

/**
 * Delete a single mapping (for Admin UI).
 */
export async function deleteMapping(mappingId: string): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase
      .from('organisation_data_mappings')
      .delete()
      .eq('id', mappingId);

    if (error) return { error: error.message };
    return { error: null };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Unknown error';
    return { error: message };
  }
}
